import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import "../styles/ResumeHistoryImport.css";

export default function ResumeHistoryImport({ onResults, disabled = false, uploadPath = "/api/candidate/resume-history/extract" }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const controllerRef = useRef(null);
  useEffect(() => () => controllerRef.current?.abort(), []);

  const analyze = async () => {
    if (!file || busy || disabled) return;
    if (!/\.(pdf|docx|txt)$/i.test(file.name) || file.size > 5 * 1024 * 1024) {
      setError("Choose a PDF, Word (.docx), or text file up to 5 MB.");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const body = new FormData();
      body.append("resume", file);
      const { data } = await axios.post(`${process.env.REACT_APP_API_BASE_URL}${uploadPath}`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        timeout: 190000, signal: controller.signal,
      });
      if (data.entries?.length) onResults(data.entries);
      else setMessage("No history found. Please enter it manually.");
    } catch (err) {
      if (!controller.signal.aborted) {
        const message = err.response?.data?.message;
        const authFailure = err.response?.status === 401 ||
          (err.response?.status === 403 && /invalid token|expired|no token/i.test(message || ""));
        setError(authFailure ? "Your sign-in has expired or is invalid. Please sign in again, then upload your resume."
          : message || "Could not analyze your resume. Please try again or enter history manually.");
      }
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  return (
    <Card className="resume-history-import my-3 text-start">
      <Card.Body>
        <Card.Title>Fill history from your resume</Card.Title>
        <p>Upload your resume to autofill work and school history. Review the details and fill any gaps.</p>
        <Form.Group controlId="resume-history-upload">
          <Form.Label>Resume <span className="text-muted small">(PDF, DOCX, or TXT · Max 5 MB)</span></Form.Label>
          <div className="d-flex align-items-center gap-2">
          <Form.Control type="file" accept=".pdf,.docx,.txt" style={{ minWidth: 0 }} disabled={busy || disabled} onChange={(event) => {
            setFile(event.target.files?.[0] || null);
            setError("");
          }} />
        <Button type="button" className="flex-shrink-0 text-nowrap" disabled={!file || busy || disabled} onClick={analyze}>
          {busy ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />Processing...</> : "Upload"}
        </Button>
          </div>
        </Form.Group>
        {busy && <p className="mt-2" role="status">Preparing your history...</p>}
        {error && <Alert variant="danger" className="mt-3" role="alert">{error}</Alert>}
        {message && <Alert variant="info" className="mt-3" role="status">{message}</Alert>}
      </Card.Body>
    </Card>
  );
}
